import type { ForceGraph3DInstance, NodeObject, LinkObject } from "3d-force-graph";
import { Vector3 } from "three";

export const getLabelOpacityUpdater = (
  Graph: ForceGraph3DInstance<NodeObject, LinkObject<NodeObject>>,
  nodeElements: Map<string, HTMLElement>,
  score: () => number,
) => {
  let lastPosition: Vector3 = new Vector3(0, 0, 0);
  let lastScore = 0;

  const updateLabelOpacity = (force = false) => {
    const camera = Graph.camera();
    const cameraPosition = camera.position;

    if (lastPosition.equals(cameraPosition) && lastScore === score() && !force) return;
    lastPosition = cameraPosition.clone();
    lastScore = score();

    // Get all nodes from the graph
    const nodes = Graph.graphData().nodes;

    if (!nodes) return;

    // Calculate distances, track closest node, and cache positions
    let closestNodeId: string | null = null;
    let closestDistance = Number.POSITIVE_INFINITY;
    const nodePositions = new Map<string, { x: number; y: number; z: number }>();

    const cameraDir = camera.getWorldDirection(new Vector3());
    nodes.forEach((node, index) => {
      const element = nodeElements.get(node.id as string)?.children[0] as HTMLDivElement;
      if (element && index > score()) {
        element.style.opacity = "0";
        return;
      }

      if (node.x !== undefined && node.y !== undefined && node.z !== undefined) {
        // Check if node is in front of the camera
        const nodeVec = new Vector3(node.x, node.y, node.z);
        const cameraToNode = nodeVec.clone().sub(cameraPosition);
        // If dot product is <= 0, node is behind the camera
        if (cameraToNode.dot(cameraDir) <= 0) {
          return;
        }

        const distance = Math.sqrt(
          (cameraPosition.x - node.x) ** 2 + (cameraPosition.y - node.y) ** 2 + (cameraPosition.z - node.z) ** 2,
        );
        nodePositions.set(node.id as string, { x: node.x, y: node.y, z: node.z });
        if (distance < closestDistance) {
          closestDistance = distance;
          closestNodeId = node.id as string;
        }
      }
    });

    if (!closestNodeId || closestDistance === Number.POSITIVE_INFINITY) return;

    // Update opacity for each node relative to the closest node (closest = 1)
    nodes.forEach((node, index) => {
      const element = nodeElements.get(node.id as string)?.children[0] as HTMLDivElement;
      if (element && index > score()) {
        element.style.opacity = "0";
        return;
      }

      if (element && nodePositions.has(node.id as string)) {
        if ((node.id as string) === closestNodeId) {
          element.style.opacity = "1";
          return;
        }

        const nodePos = nodePositions.get(node.id as string)!;
        const distance = Math.sqrt(
          (cameraPosition.x - nodePos.x) ** 2 +
            (cameraPosition.y - nodePos.y) ** 2 +
            (cameraPosition.z - nodePos.z) ** 2,
        );

        // Opacity scales relative to the closest node (exponential fall-off)
        const relative = closestDistance > 0 ? closestDistance / distance : 1;
        const opacity = Math.min(1, Math.max(0.1, relative ** 3));
        const scale = Math.min(1, Math.max(0.1, relative));

        element.style.opacity = opacity.toString();
        element.style.transform = `scale(${scale})`;
      }
    });
  };

  return updateLabelOpacity;
};
